import axios from "axios";
import Cookies from "js-cookie";
import {
  getActiveOfficeCodeFromStorage,
  getAssignedOfficeCodesFromStorage,
  resolveAssignableOrganizations,
} from "../../../utils/officeCodeContext";
import { normalizeYardSettingsResponse } from "./yardViewUtils";
import { fetchLocationByRef } from "../Appointments/TabContent/api/AppointmentsApi";
import {
  looksLikeWarehouseRefUuid,
  resolveOfficeCodeFromAppointment,
  resolveOfficeCodeFromLocation,
  resolveWarehouseLocationRefForDocks,
} from "./checkInGateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getAuthToken = () =>
  localStorage.getItem("authToken") || Cookies.get("authToken");

export const getOfficeCode = () => getActiveOfficeCodeFromStorage();

/** Active grid office for YMS gate check-in (e.g. DIAMOND — need not match appointment office exactly). */
export function resolveYmsGridOfficeCode() {
  return String(getOfficeCode() ?? "").trim();
}

/** officeCode for check-out: prefer the trailer row from /api/trailers/grid, else active grid office. */
export function resolveCheckoutOfficeCode(trailer) {
  const fromRow = String(
    trailer?.officeCode ?? trailer?.office_code ?? ""
  ).trim();
  return fromRow || resolveYmsGridOfficeCode();
}

/** All office codes the signed-in user can access (cookie + organizationData). */
export function resolveTrailerOfficeCodes(explicitCodes) {
  const fromArg = (explicitCodes || [])
    .flatMap((code) => String(code).split(","))
    .map((code) => code.trim())
    .filter(Boolean);
  if (fromArg.length) return [...new Set(fromArg)];

  const fromCookie = getAssignedOfficeCodesFromStorage()
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);
  if (fromCookie.length) return [...new Set(fromCookie)];

  const fromOrgs = resolveAssignableOrganizations()
    .map((org) => String(org.officeCode || "").trim())
    .filter(Boolean);
  if (fromOrgs.length) return [...new Set(fromOrgs)];

  const active = getActiveOfficeCodeFromStorage();
  return active ? [active] : [];
}

export function formatTrailerOfficeCodesParam(explicitCodes) {
  return resolveTrailerOfficeCodes(explicitCodes).join(",");
}

/** Query params + headers for yard-settings GET (all accessible offices). */
export function yardSettingsOfficeCodesRequest(explicitCodes) {
  const office_codes = formatTrailerOfficeCodesParam(explicitCodes);
  return {
    office_codes,
    params: office_codes ? { office_codes } : {},
    headerFields: office_codes ? { "Office-Code": office_codes } : {},
  };
}

function resolveYardSettingsWriteOfficeCode(explicitCode) {
  if (explicitCode != null && String(explicitCode).trim() !== "") {
    const single = String(explicitCode)
      .split(",")
      .map((code) => code.trim())
      .find(Boolean);
    if (single) return single;
  }
  return String(getOfficeCode() ?? "").trim();
}

/** Query params + headers for yard-settings POST/PUT (single office only). */
export function yardSettingsWriteOfficeCodesRequest(explicitCode) {
  const office_codes = resolveYardSettingsWriteOfficeCode(explicitCode);
  return {
    office_codes,
    params: office_codes ? { office_codes } : {},
    headerFields: office_codes ? { "Office-Code": office_codes } : {},
  };
}

export const moveTrailer = async ({
  trailerId,
  toBayId,
  toDockId,
  toDockLabel,
  notes,
  status,
  sealVerificationNotes,
  sealDiscrepancy,
  supervisorBypassReason,
}) => {
  const token = getAuthToken();
  const movedByUserId = JSON.parse(Cookies.get("userData") || "{}").id ?? null;

  const response = await axios.post(
    `${API_BASE_URL}/api/trailers/move`,
    {
      trailerId,
      toBayId:       toBayId   ? Number(toBayId)   : null,
      toDockId:      toDockId  ? Number(toDockId)  : null,
      // Dock name from the Appointment module's own dock list — only sent when neither numeric
      // id above is known yet; backend resolves (and creates the yms_yard_dock occupancy row on
      // first use) the same way check-in's dockBay field does.
      toDockLabel:   !toBayId && !toDockId ? (toDockLabel || null) : null,
      movedByUserId: movedByUserId ? Number(movedByUserId) : null,
      notes:         notes || null,
      status:        status    || null,
      sealVerificationNotes: sealVerificationNotes || null,
      sealDiscrepancy: sealDiscrepancy || null,
      supervisorBypassReason: supervisorBypassReason || null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

/** PATCH /api/trailers/{trailerId}/status — standalone status edit, no bay/dock change. */
export const updateTrailerStatus = async (trailerId, status, performedByUserId) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/status`,
    { status, ...(performedByUserId != null ? { performedByUserId } : {}) },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const fetchTrailerById = async (trailerId) => {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/api/trailers/${trailerId}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const fetchTrailersGrid = async (officeCodesOverride) => {
  const token = getAuthToken();
  const officeCode = formatTrailerOfficeCodesParam(officeCodesOverride);

  const response = await axios.get(`${API_BASE_URL}/api/trailers/grid`, {
    params: officeCode ? { officeCode } : {},
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(officeCode ? { "Office-Code": officeCode } : {}),
    },
  });

  return response.data;
};

/**
 * Gate check-in search: appointmentId or shipmentNumber + officeCode → { eligibility, gateDetails }.
 * `lookupSkipped` requires YMS:OVERRIDE_POST (403 otherwise).
 */
export const lookupGateCheckIn = async ({
  appointmentId,
  shipmentNumber,
  officeCode,
  lookupSkipped,
} = {}) => {
  const token = getAuthToken();
  const oc = String(officeCode ?? getOfficeCode()).trim();
  const params = {};
  if (oc) params.officeCode = oc;
  if (appointmentId != null && String(appointmentId).trim() !== "") {
    params.appointmentId = String(appointmentId).trim();
  }
  if (shipmentNumber != null && String(shipmentNumber).trim() !== "") {
    params.shipmentNumber = String(shipmentNumber).trim();
  }
  if (lookupSkipped) params.lookupSkipped = true;

  const response = await axios.get(`${API_BASE_URL}/api/trailers/gate-check-in/lookup`, {
    params,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/** Partial-match shipment search for yard check-in type-ahead (POST /api/shipment-details/partial-search). */
export const fetchShipmentPartialSearch = async (
  query,
  officeCode,
  { page = 0, size = 8 } = {}
) => {
  const token = getAuthToken();
  const oc = String(officeCode ?? getOfficeCode() ?? "").trim();
  const q = String(query ?? "").trim();
  if (!q) return { content: [], totalElements: 0 };

  const response = await axios.post(
    `${API_BASE_URL}/api/shipment-details/partial-search?page=${page}&size=${size}`,
    {
      query: q,
      sortBy: "shipmentNumber",
      filterType: "ALL",
      ascending: true,
      status: null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(oc ? { "Office-Code": oc } : {}),
      },
    }
  );

  return response.data;
};

/** Yard bay/dock inventory for check-in spot IDs (selectedSpotId / currentBayId / currentDockId) and occupancy hints. */
export const fetchTrailerLocations = async (officeCodesOverride) => {
  const token = getAuthToken();
  const office_codes = formatTrailerOfficeCodesParam(officeCodesOverride);
  const response = await axios.get(`${API_BASE_URL}/api/trailers/locations`, {
    params: office_codes ? { office_codes } : {},
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(office_codes ? { "Office-Code": office_codes } : {}),
    },
  });
  return response.data;
};

/** JWT → YmsCapabilitiesDTO (view, checkIn, move, supervisorOverride, …). */
export const fetchYmsCapabilities = async () => {
  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/trailers/me/capabilities`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/** True when JWT grants supervisor gate override (skip eligibility lookup on check-in). */
export function ymsHasSupervisorOverridePost(caps) {
  if (!caps || typeof caps !== "object") return false;
  const v =
    caps.supervisorOverride ??
    caps.supervisor_override ??
    caps.supervisorOverridePost ??
    caps.ymsOverridePost;
  return v === true || v === "true";
}

/**
 * Download location history CSV for a trailer.
 * @returns {{ blob: Blob, filename: string }}
 */
export const downloadTrailerLocationHistoryExport = async (trailerId) => {
  const token = getAuthToken();
  const response = await axios.get(
    `${API_BASE_URL}/api/trailers/${encodeURIComponent(trailerId)}/location-history/export`,
    {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const dispo = response.headers["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i.exec(dispo);
  const filename = match ? decodeURIComponent(match[1]) : `trailer-${trailerId}-location-history.csv`;
  return { blob: response.data, filename };
};

/**
 * Multipart part names for POST /api/trailers/check-in ({@link TrailerController#checkInTrailerMultipart}).
 */
export const TRAILER_CHECK_IN_MULTIPART_PARTS = {
  driverLicenseImage: "driverLicenseImage",
  sealPhoto: "sealPhoto",
};

/**
 * Build FormData for gate check-in with optional DL and seal photo files.
 * @param {Record<string, unknown>} fields - scalar TrailerCheckInRequest fields
 * @param {{ driverLicenseImage?: Blob, sealPhoto?: Blob, sealImage?: Blob }} files - sealImage alias accepted for sealPhoto
 */
export function buildTrailerCheckInFormData(fields, files = {}) {
  const fd = new FormData();
  for (const [key, val] of Object.entries(fields)) {
    if (val == null || val === "") continue;
    if (typeof val === "boolean") {
      fd.append(key, val ? "true" : "false");
      continue;
    }
    if (typeof val === "number") {
      fd.append(key, String(val));
      continue;
    }
    fd.append(key, val);
  }

  const dl = files.driverLicenseImage;
  if (dl instanceof Blob) {
    const name = dl instanceof File && dl.name ? dl.name : "driver-license";
    fd.append(TRAILER_CHECK_IN_MULTIPART_PARTS.driverLicenseImage, dl, name);
  }

  const seal = files.sealPhoto ?? files.sealImage;
  if (seal instanceof Blob) {
    const name = seal instanceof File && seal.name ? seal.name : "seal";
    fd.append(TRAILER_CHECK_IN_MULTIPART_PARTS.sealPhoto, seal, name);
  }

  return fd;
}

/**
 * Yard gate check-in: POST /api/trailers/check-in — persists trailer, updates appointment, uploads DL/seal images to S3 when files provided.
 * @param {Record<string, unknown>} payload - TrailerCheckInRequest scalars (appointmentId, officeCode, trailerNumber, …)
 * @param {{ driverLicenseImage?: Blob, sealPhoto?: Blob } | null} files - optional; triggers multipart when any blob present
 */
export const checkInTrailer = async (payload = {}, files = null) =>
  postGateCheckIn(`${API_BASE_URL}/api/trailers/check-in`, payload, files);

/**
 * Create-mode gate check-in: POST /api/trailers/walk-up-check-in (LGX-1104) — creates the Shipment
 * for walk-up freight that has no record yet AND checks the trailer in against it, in one
 * transaction server-side.
 *
 * Deliberately a different endpoint from {@link checkInTrailer} rather than a flag: two client
 * calls would be two transactions, and the pair has to succeed or fail as a unit. Send no
 * appointmentId (the server rejects one here) and normally no shipmentNumber — the server
 * generates it for the office.
 *
 * @param {Record<string, unknown>} payload - WalkUpCheckInRequest scalars: everything
 *   checkInTrailer takes, plus bolNumber and the origin/destination address fields
 * @param {{ driverLicenseImage?: Blob, sealPhoto?: Blob } | null} files - triggers multipart
 */
export const checkInWalkUp = async (payload = {}, files = null) =>
  postGateCheckIn(`${API_BASE_URL}/api/trailers/walk-up-check-in`, payload, files);

/**
 * Shared POST for both gate check-in endpoints — same office-code resolution, same
 * multipart-when-files-present switch. Split out so the walk-up variant cannot drift from the
 * matched-search one on any of that.
 */
async function postGateCheckIn(url, payload, files) {
  const token = getAuthToken();
  const gridOfficeCode = resolveYmsGridOfficeCode();
  const officeCode = String(payload.officeCode ?? gridOfficeCode ?? "").trim();
  const fields = {
    ...payload,
    ...(officeCode ? { officeCode } : {}),
  };

  const hasFiles =
    files &&
    ((files.driverLicenseImage instanceof Blob && files.driverLicenseImage.size > 0) ||
      (files.sealPhoto instanceof Blob && files.sealPhoto.size > 0));

  if (hasFiles) {
    const fd = buildTrailerCheckInFormData(fields, files);
    const response = await axios.post(url, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(officeCode ? { "Office-Code": officeCode } : {}),
      },
    });
    return response.data;
  }

  const response = await axios.post(url, fields, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(officeCode ? { "Office-Code": officeCode } : {}),
    },
  });

  return response.data;
}

/**
 * Create a trailer row only (POST /api/trailers). Does not run gate check-in or save DL/seal media — use {@link checkInTrailer} from the appointment modal.
 */
export const createTrailer = async (payload = {}) => {
  const token = getAuthToken();
  const officeCode = getOfficeCode();

  const body = {
    ...payload,
    ...(officeCode && payload.officeCode == null ? { officeCode } : {}),
  };

  const response = await axios.post(`${API_BASE_URL}/api/trailers`, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(officeCode ? { officeCode } : {}),
    },
  });

  return response.data;
};

/**
 * Yard gate check-out — sends officeCode from the grid trailer row when available.
 * `extra.driverName`/`extra.driverLicenseNumber` capture the PICKUP driver, who may be a
 * different person than whoever dropped the trailer off.
 */
export const checkoutTrailer = async (trailerId, trailer = null, extra = {}) => {
  const token = getAuthToken();
  const officeCode = resolveCheckoutOfficeCode(trailer);
  const scopeHeader = formatTrailerOfficeCodesParam();

  const body = {
    ...(officeCode ? { officeCode } : {}),
    ...(extra.driverName ? { driverName: extra.driverName } : {}),
    ...(extra.driverLicenseNumber ? { driverLicenseNumber: extra.driverLicenseNumber } : {}),
    ...(extra.driverEmail ? { driverEmail: extra.driverEmail } : {}),
    ...(extra.driverPhone ? { driverPhone: extra.driverPhone } : {}),
    ...(extra.driverLicenseState ? { driverLicenseState: extra.driverLicenseState } : {}),
    ...(extra.driverLicenseExpiry ? { driverLicenseExpiry: extra.driverLicenseExpiry } : {}),
    ...(extra.sealNumber ? { sealNumber: extra.sealNumber } : {}),
    ...(extra.checkoutWeight ? { checkoutWeight: extra.checkoutWeight } : {}),
    ...(extra.checkoutVolume ? { checkoutVolume: extra.checkoutVolume } : {}),
    ...(extra.checkoutPallets ? { checkoutPallets: extra.checkoutPallets } : {}),
    ...(extra.checkoutWeightUnit ? { checkoutWeightUnit: extra.checkoutWeightUnit } : {}),
    ...(extra.checkoutVolumeUnit ? { checkoutVolumeUnit: extra.checkoutVolumeUnit } : {}),
  };

  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/checkout`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(scopeHeader ? { "Office-Code": scopeHeader } : {}),
      },
    }
  );

  return response.data;
};

/**
 * PATCH /api/trailers/{trailerId}/checkout-actuals — dispatcher "Complete Loading"/
 * "Complete Unloading": saves weight/volume/pallets/seal + pickup driver info WITHOUT
 * releasing the gate (trailer stays CHECKED_IN). Same body shape as {@link checkoutTrailer}.
 */
export const saveCheckoutActuals = async (trailerId, trailer = null, extra = {}) => {
  const token = getAuthToken();
  const officeCode = resolveCheckoutOfficeCode(trailer);
  const scopeHeader = formatTrailerOfficeCodesParam();

  const body = {
    ...(officeCode ? { officeCode } : {}),
    ...(extra.driverName ? { driverName: extra.driverName } : {}),
    ...(extra.driverLicenseNumber ? { driverLicenseNumber: extra.driverLicenseNumber } : {}),
    ...(extra.driverEmail ? { driverEmail: extra.driverEmail } : {}),
    ...(extra.driverPhone ? { driverPhone: extra.driverPhone } : {}),
    ...(extra.driverLicenseState ? { driverLicenseState: extra.driverLicenseState } : {}),
    ...(extra.driverLicenseExpiry ? { driverLicenseExpiry: extra.driverLicenseExpiry } : {}),
    ...(extra.sealNumber ? { sealNumber: extra.sealNumber } : {}),
    ...(extra.checkoutWeight ? { checkoutWeight: extra.checkoutWeight } : {}),
    ...(extra.checkoutVolume ? { checkoutVolume: extra.checkoutVolume } : {}),
    ...(extra.checkoutPallets ? { checkoutPallets: extra.checkoutPallets } : {}),
    ...(extra.checkoutWeightUnit ? { checkoutWeightUnit: extra.checkoutWeightUnit } : {}),
    ...(extra.checkoutVolumeUnit ? { checkoutVolumeUnit: extra.checkoutVolumeUnit } : {}),
  };

  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/checkout-actuals`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(scopeHeader ? { "Office-Code": scopeHeader } : {}),
      },
    }
  );

  return response.data;
};

/**
 * PATCH /api/trailers/locations/availability — mark a bay or dock unavailable/available.
 */
export const updateYardSlotAvailability = async ({
  officeCode,
  warehouseRef,
  sectionName,
  slotType,
  slotName,
  unavailable,
  bayId,
  dockId,
} = {}) => {
  const token = getAuthToken();
  const oc = String(officeCode ?? getOfficeCode()).trim();

  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/locations/availability`,
    {
      officeCode: oc,
      warehouseRef: String(warehouseRef ?? "").trim() || undefined,
      sectionName: String(sectionName ?? "").trim() || undefined,
      slotType: String(slotType ?? "").trim().toUpperCase() || undefined,
      slotName: String(slotName ?? "").trim() || undefined,
      unavailable: !!unavailable,
      bayId: bayId != null && bayId !== "" ? Number(bayId) : undefined,
      dockId: dockId != null && dockId !== "" ? Number(dockId) : undefined,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(oc ? { officeCode: oc } : {}),
      },
    }
  );

  return response.data;
};

let groupedLocationsCache = null;
let groupedLocationsInflight = null;

async function loadGroupedLocationsForCheckIn() {
  if (groupedLocationsCache) return groupedLocationsCache;
  if (!groupedLocationsInflight) {
    groupedLocationsInflight = (async () => {
      const token = getAuthToken();
      // GET /api/locations/grouped is backed by findAllSharedLocations(), which explicitly
      // queries `WHERE office_code IS NULL` — it can NEVER return an office-scoped warehouse
      // like a real yard facility, by design (it's for shared/unscoped locations only). Using
      // it here to resolve a warehouse name -> office code was the wrong endpoint entirely,
      // not just missing a header: it silently matched onto an orphaned duplicate location row
      // (same name, office_code NULL) instead of the real one. by-office-codes is scoped by
      // officeCodes IN (...), so it naturally excludes NULL-office rows and only returns the
      // genuine, correctly-seeded location — same endpoint yardmanagement.js already uses.
      const codes = resolveTrailerOfficeCodes();
      const params = new URLSearchParams();
      codes.forEach((c) => params.append("officeCodes", c));
      const response = await axios.get(`${API_BASE_URL}/api/locations/by-office-codes`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
      });
      groupedLocationsCache = Array.isArray(response.data) ? response.data : [];
      return groupedLocationsCache;
    })()
      .catch(() => {
        groupedLocationsCache = [];
        return [];
      })
      .finally(() => {
        groupedLocationsInflight = null;
      });
  }
  return groupedLocationsInflight;
}

function warehouseDisplayNamesMatch(a, b) {
  const x = String(a ?? "").trim().toLowerCase();
  const y = String(b ?? "").trim().toLowerCase();
  if (!x || !y) return false;
  return x === y;
}

/**
 * Resolve warehouse locationRef + office code from the warehouse selected on check-in.
 * Used to scope yard settings, /api/trailers/locations, docks, and POST /api/trailers/check-in.
 */
// Jackson's @JsonIdentityInfo on Location.parentLocation collapses a repeated reference to a
// bare id number the 2nd+ time it appears in a response (see locationHierarchy.js in the
// Manage-Team module for the same behavior) -- resolve it against the full grouped list so a
// bare-id parent still finds its real object instead of failing to match anything.
function resolveParentLocationFromGrouped(loc, grouped) {
  const parent = loc?.parentLocation;
  if (parent == null) return null;
  if (typeof parent === "object") return parent;
  return grouped.find((l) => String(l?.id) === String(parent)) ?? null;
}

export async function resolveWarehouseContextForCheckIn({
  warehouseName = "",
  warehouseLocationRef = "",
  appointment = null,
} = {}) {
  const whName = String(warehouseName ?? "").trim();
  let locationRef = String(
    warehouseLocationRef ||
      resolveWarehouseLocationRefForDocks(appointment, whName) ||
      ""
  ).trim();
  let officeCode = resolveOfficeCodeFromAppointment(appointment);
  let location = null;
  // The specific WAREHOUSE-typed location actually tied to this appointment, if the resolved
  // match turns out to be a warehouse leaf rather than the plant -- see below.
  let resolvedWarehouse = null;

  const applyLocation = (loc) => {
    if (!loc || typeof loc !== "object") return;
    location = loc;
    const oc = resolveOfficeCodeFromLocation(loc);
    if (oc) officeCode = oc;
    const ref = String(loc.locationRef ?? "").trim();
    if (ref) locationRef = ref;
  };

  if (looksLikeWarehouseRefUuid(locationRef)) {
    try {
      applyLocation(await fetchLocationByRef(locationRef));
    } catch {
      /* fall through */
    }
  }

  if (whName && (!location || !officeCode || !locationRef)) {
    try {
      const grouped = await loadGroupedLocationsForCheckIn();
      const match =
        grouped.find((loc) => warehouseDisplayNamesMatch(loc.locationName, whName)) ??
        grouped.find((loc) => {
          const n = String(loc.locationName ?? "").trim().toLowerCase();
          const t = whName.toLowerCase();
          return n && (n.includes(t) || t.includes(n));
        });
      if (match) applyLocation(match);
    } catch {
      /* fall through */
    }
  }

  // A warehouse-typed location has no WAREHOUSE children of its own -- if the appointment's
  // warehouse name/ref resolved directly to one specific warehouse (e.g. "Roth Warehouse")
  // rather than its parent plant (e.g. "Diamond Pet Foods - Lathrop"), fetching *its* children
  // for the dock-path warehouse dropdown silently returns nothing, leaving the dropdown empty
  // with no docks selectable even though a perfectly good warehouse is already known. Climb to
  // the parent plant instead, so the dropdown lists every sibling warehouse (Harlan + Roth) and
  // the caller can pre-select this one specifically via the returned resolvedWarehouse.
  if (location && String(location.locationType ?? "").toUpperCase() === "WAREHOUSE") {
    try {
      const grouped = await loadGroupedLocationsForCheckIn();
      const parent = resolveParentLocationFromGrouped(location, grouped);
      if (parent) {
        resolvedWarehouse = location;
        applyLocation(parent);
      }
    } catch {
      /* fall through — keep the warehouse itself as location rather than losing it entirely */
    }
  }

  return {
    officeCode: String(officeCode ?? "").trim(),
    locationRef,
    location,
    resolvedWarehouse,
  };
}

/**
 * Child locations of a plant/facility (e.g. its WAREHOUSE rows) — same call CalendarSettings.js
 * and yardsettingstab.js already use for the plant -> warehouse relationship.
 */
export const fetchLocationChildren = async (parentId) => {
  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/locations/children/by-id`, {
    params: { parentId },
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "*/*",
    },
  });
  return Array.isArray(response.data) ? response.data : [];
};

// Returns dock objects: [{ dockId, dockName, ... }]
export const fetchDocksForWarehouse = async (warehouseRef) => {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/api/dock/docks/${warehouseRef}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};


/**
 * Yard layout settings for a warehouse.
 * `warehouseKey` = warehouse display name (URL-encoded path segment), not locationRef / UUID.
 */
export const fetchYardSettings = async (warehouseKey, officeCodesOverride) => {
  const token = getAuthToken();
  const path = encodeURIComponent(String(warehouseKey ?? "").trim());
  const { params, headerFields } = yardSettingsOfficeCodesRequest(officeCodesOverride);

  try {
    const response = await axios.get(`${API_BASE_URL}/api/yard-settings/${path}`, {
      params,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...headerFields,
      },
    });
    return normalizeYardSettingsResponse(response.data);
  } catch (err) {
    if (err?.response?.status === 404) {
      return { ...normalizeYardSettingsResponse({ sections: [] }), _isNew: true };
    }
    throw err;
  }
};

/** YMS gate lookup for yard check-in search. */
export const fetchYmsGateDetails = async (gateDetailsId, officeCode) => {
  const token = getAuthToken();
  const oc = (officeCode || getOfficeCode()).trim();
  if (!oc) {
    const err = new Error("MISSING_OFFICE_CODE");
    err.code = "MISSING_OFFICE_CODE";
    throw err;
  }

  const response = await axios.get(`${API_BASE_URL}/api/yms/gate-details`, {
    params: { id: gateDetailsId, officeCode: oc },
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  return response.data;
};

// ─── Hostler Jobs ──────────────────────────────
// Name-based throughout, matching the backend: a Hostler is referenced by email (not a numeric
// user id) and a destination by its bay/dock NAME (toSpotLabel), never a numeric bay/dock id —
// see TrailerServiceImpl's Hostler Jobs section for the full rationale (numeric ids from another
// table/service don't survive independently-seeded environments or a future service split).

/** Dispatch's consolidated view: every open Hostler job across the given offices. */
export const fetchHostlerJobQueue = async (officeCodesOverride) => {
  const token = getAuthToken();
  const office_codes = formatTrailerOfficeCodesParam(officeCodesOverride);
  const response = await axios.get(`${API_BASE_URL}/api/trailers/hostler-jobs/queue`, {
    params: office_codes ? { office_codes } : {},
    headers: {
      Authorization: `Bearer ${token}`,
      ...(office_codes ? { "Office-Code": office_codes } : {}),
    },
  });
  return response.data;
};

/** "Hostlers on shift" — every user holding the Yard Hostler role at the given offices, idle or busy. */
export const fetchHostlerRoster = async (officeCodesOverride) => {
  const token = getAuthToken();
  const office_codes = formatTrailerOfficeCodesParam(officeCodesOverride);
  const response = await axios.get(`${API_BASE_URL}/api/trailers/hostler-jobs/roster`, {
    params: office_codes ? { office_codes } : {},
    headers: {
      Authorization: `Bearer ${token}`,
      ...(office_codes ? { "Office-Code": office_codes } : {}),
    },
  });
  return response.data;
};

/** The logged-in Hostler's own assigned jobs. */
export const fetchMyHostlerJobs = async () => {
  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/trailers/hostler-jobs/mine`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/** Unclaimed jobs a Hostler can self-claim, scoped to their own offices. */
export const fetchClaimableHostlerJobs = async (officeCodesOverride) => {
  const token = getAuthToken();
  const office_codes = formatTrailerOfficeCodesParam(officeCodesOverride);
  const response = await axios.get(`${API_BASE_URL}/api/trailers/hostler-jobs/available`, {
    params: office_codes ? { office_codes } : {},
    headers: {
      Authorization: `Bearer ${token}`,
      ...(office_codes ? { "Office-Code": office_codes } : {}),
    },
  });
  return response.data;
};

/** Dispatch creates a job, optionally pre-assigned to a Hostler (by email). */
export const createHostlerJob = async ({
  trailerId,
  toSpotLabel,
  toWarehouseRef,
  assignToHostlerEmail,
  note,
}) => {
  const token = getAuthToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/trailers/hostler-jobs`,
    {
      trailerId,
      toSpotLabel: toSpotLabel || null,
      toWarehouseRef: toWarehouseRef || null,
      assignToHostlerEmail: assignToHostlerEmail || null,
      note: note || null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/** A Hostler claims an unassigned job from the shared queue. */
export const claimHostlerJob = async (trailerId) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/hostler-jobs/${trailerId}/claim`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

/** A Hostler completes their own job — performs the actual move server-side. */
export const completeHostlerJob = async (trailerId, note) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/hostler-jobs/${trailerId}/complete`,
    { note: note || null },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/** Dispatch override — reassign, change destination (incl. cross-warehouse), force-complete, or cancel. Always requires a note. */
export const overrideHostlerJob = async ({
  trailerId,
  action,
  reassignToHostlerEmail,
  toSpotLabel,
  toWarehouseRef,
  note,
}) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/hostler-jobs/override`,
    {
      trailerId,
      action,
      reassignToHostlerEmail: reassignToHostlerEmail || null,
      toSpotLabel: toSpotLabel || null,
      toWarehouseRef: toWarehouseRef || null,
      note,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Report an issue on a trailer — flat tire/damage/leak (safety, photo required client-side) or
 * wrong trailer/blocked spot/missing paperwork (administrative, photo optional). Uses multipart
 * only when a photo is attached.
 */
export const reportTrailerIssue = async (trailerId, { issueType, notes, photo } = {}) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}/api/trailers/${trailerId}/issues`;

  if (photo instanceof Blob && photo.size > 0) {
    const fd = new FormData();
    fd.append("issueType", issueType);
    if (notes) fd.append("notes", notes);
    fd.append("photo", photo, photo instanceof File && photo.name ? photo.name : "issue-photo");
    const response = await axios.post(url, fd, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  const response = await axios.post(
    url,
    { issueType, notes: notes || null },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/** Dispatch clears a previously reported issue. */
export const clearTrailerIssue = async (trailerId, note) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/issues/clear`,
    { note: note || null },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Records a pre-load/post-load/pre-unload/post-unload inspection on a trailer. REJECTED
 * requires rejectionCode (+ rejectionCodeOther when code is "OTHER") and a photo. Uses
 * multipart only when a photo is attached, same pattern as reportTrailerIssue.
 */
export const recordTrailerInspection = async (
  trailerId,
  { checkpointType, outcome, rejectionCode, rejectionCodeOther, notes, photo } = {}
) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}/api/trailers/${trailerId}/inspections`;

  if (photo instanceof Blob && photo.size > 0) {
    const fd = new FormData();
    fd.append("checkpointType", checkpointType);
    fd.append("outcome", outcome);
    if (rejectionCode) fd.append("rejectionCode", rejectionCode);
    if (rejectionCodeOther) fd.append("rejectionCodeOther", rejectionCodeOther);
    if (notes) fd.append("notes", notes);
    fd.append("photo", photo, photo instanceof File && photo.name ? photo.name : "inspection-photo");
    const response = await axios.post(url, fd, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  const response = await axios.post(
    url,
    {
      checkpointType,
      outcome,
      rejectionCode: rejectionCode || null,
      rejectionCodeOther: rejectionCodeOther || null,
      notes: notes || null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/** Full inspection history attached to a trailer, most recent first. */
export const fetchTrailerInspections = async (trailerId) => {
  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/trailers/${trailerId}/inspections`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/** Supervisor clears a REJECTED hold, allowing the trailer to move/checkout again. */
export const clearTrailerHold = async (trailerId, note) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/inspections/clear-hold`,
    { note: note || null },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Outbound Drop Trailer only: the pickup driver has arrived at the gate. Syncs to the shipment's
 * gate-in actual. `occurredAt` (ISO local datetime string, no zone suffix) lets the dispatcher
 * backdate the moment being logged when they weren't at the gate when it happened; omit for now.
 */
export const recordPickupArrival = async (trailerId, occurredAt) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/pickup-arrival`,
    occurredAt ? { occurredAt } : {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

/**
 * Inbound Drop Trailer only: the drop-off driver is leaving the gate bobtail. Syncs to the
 * shipment's gate-out actual. `occurredAt` (ISO local datetime string, no zone suffix) lets the
 * dispatcher backdate the moment being logged when they weren't at the gate when it happened;
 * omit for now.
 */
export const recordDropoffDeparture = async (trailerId, occurredAt) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/api/trailers/${trailerId}/dropoff-departure`,
    occurredAt ? { occurredAt } : {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

/** Optional: direct appointment check-in (not used by Complete Check In — that flows through POST /api/trailers/check-in). */
export const checkInAppointmentAtYard = async (appointmentId, body = {}) => {
  const token = getAuthToken();
  const auth = token ? { Authorization: `Bearer ${token}` } : {};

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const response = await axios.post(
      `${API_BASE_URL}/api/appointment/${appointmentId}/check-in`,
      body,
      { headers: { ...auth } }
    );
    return response.data;
  }

  const response = await axios.post(
    `${API_BASE_URL}/api/appointment/${appointmentId}/check-in`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
        ...auth,
      },
    }
  );

  return response.data;
};

/**
 * Type-ahead over the office's real facilities, for the walk-up check-in's Origin Name /
 * Destination Name fields. A walk-up has no appointment to seed those from, so before this the
 * guard retyped a name and address that almost always already exists as a Location — producing
 * near-duplicate stop addresses that nothing downstream can match on.
 * <p>
 * /search-facilities (not the grouped list) because it is the endpoint built for exactly this:
 * server-side filtered, capped at 10, office-scoped, and it excludes the GEOGRAPHY hierarchy
 * rows that are rate-shopping graph nodes rather than places freight comes from. It returns the
 * full Location, so one pick can fill name + both address lines + city + state + zip + country.
 */
/**
 * Carrier type-ahead for the gate check-in's Carrier field. No office parameter: a carrier is
 * whoever the freight moves with, not necessarily an org trading at this yard's office.
 */
export const searchCarriers = async (query) => {
  const q = String(query ?? "").trim();
  if (!q) return [];

  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/trailers/carriers/search`, {
    params: { query: q },
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "*/*",
    },
  });
  return Array.isArray(response.data) ? response.data : [];
};

export const searchFacilityLocations = async (officeCode, query) => {
  const oc = String(officeCode ?? "").trim();
  const q = String(query ?? "").trim();
  if (!oc || !q) return [];

  const token = getAuthToken();
  const response = await axios.get(`${API_BASE_URL}/api/locations/search-facilities`, {
    params: { officeCode: oc, query: q },
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "*/*",
    },
  });
  return Array.isArray(response.data) ? response.data : [];
};
